import type { AgentActionButton } from '../types/agent';
import type { AgentFormVariant, AgentShortcut } from './agentShortcut';

export type AgentPageNavigationState = {
  chatId?: number;
  agentShortcut?: string;
  agentForm?: AgentFormVariant;
  agentProjectPick?: string;
  agentAction?: { action: string; data?: Record<string, unknown> };
  agentFormPayload?: Record<string, unknown>;
  /** Walkthrough / CTA: run client-side proxy diagnostics on AI Agent mount. */
  diagnoseProxy?: boolean;
};

export const AGENT_PENDING_NAV_STORAGE_KEY = 'ai-agent-pending-navigation';
export const AGENT_PAGE_NAV_EVENT = 'ai-agent-pending-navigation';
export const AGENT_CONTEXT_SYNC_EVENT = 'ai-agent-context-sync';

export type AgentContextSyncDetail = {
  chatId: number;
  projectId?: number | null;
  source?: 'ai-agent-page' | 'widget';
};

export function dispatchAgentContextSync(detail: AgentContextSyncDetail): void {
  window.dispatchEvent(new CustomEvent(AGENT_CONTEXT_SYNC_EVENT, { detail }));
}

export function isAgentPageNavigationState(value: unknown): value is AgentPageNavigationState {
  if (!value || typeof value !== 'object') return false;
  const v = value as AgentPageNavigationState;
  return Boolean(
    v.agentForm ||
      v.agentShortcut ||
      v.agentProjectPick ||
      v.agentAction ||
      v.diagnoseProxy,
  );
}

export function persistAgentPageNavigation(state: AgentPageNavigationState): void {
  try {
    sessionStorage.setItem(AGENT_PENDING_NAV_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export function consumeAgentPageNavigation(): AgentPageNavigationState | null {
  try {
    const raw = sessionStorage.getItem(AGENT_PENDING_NAV_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AGENT_PENDING_NAV_STORAGE_KEY);
    const parsed: unknown = JSON.parse(raw);
    return isAgentPageNavigationState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function dispatchAgentPageNavigation(state: AgentPageNavigationState): void {
  persistAgentPageNavigation(state);
  window.dispatchEvent(new CustomEvent(AGENT_PAGE_NAV_EVENT, { detail: state }));
}

export function navigationStateFromShortcut(
  shortcut: AgentShortcut,
  chatId?: number | null,
): AgentPageNavigationState {
  if (shortcut.kind === 'form') {
    const state: AgentPageNavigationState = { agentForm: shortcut.variant, chatId: chatId ?? undefined };
    if (shortcut.mode) {
      state.agentFormPayload = { selectionMode: shortcut.mode };
    }
    return state;
  }
  if (shortcut.kind === 'project_pick') {
    return { agentProjectPick: shortcut.action, chatId: chatId ?? undefined };
  }
  return {
    agentAction: { action: shortcut.action, data: shortcut.data },
    chatId: chatId ?? undefined,
  };
}

export function navigationStateFromAgentAction(
  action: AgentActionButton,
  chatId?: number | null,
): AgentPageNavigationState | null {
  const payload = (action.payload ?? {}) as Record<string, unknown>;
  switch (action.id) {
    case 'create_project_import': {
      const intent = typeof payload.intent === 'string' ? payload.intent : '';
      const isImportFlow = intent === 'import_swagger';
      const isRetry =
        action.label === 'Try again' ||
        typeof payload.swaggerUrl === 'string' ||
        typeof payload.projectName === 'string' ||
        payload.projectId != null;
      if (isImportFlow || isRetry) {
        return { agentForm: 'import_swagger', agentFormPayload: payload, chatId: chatId ?? undefined };
      }
      return { agentForm: 'create_select', chatId: chatId ?? undefined };
    }
    case 'create_environment':
      return { agentForm: 'create_environment', agentFormPayload: payload, chatId: chatId ?? undefined };
    case 'add_environment_variable':
      return { agentForm: 'env_variable', agentFormPayload: payload, chatId: chatId ?? undefined };
    case 'apply_environment_variables':
    case 'apply_environment_variables_to_request':
      return { agentForm: 'apply_env_to_request', agentFormPayload: payload, chatId: chatId ?? undefined };
    case 'run_single_request':
      return { agentForm: 'execute_request', agentFormPayload: payload, chatId: chatId ?? undefined };
    case 'copy_request_to_all_collections':
      return {
        agentForm: 'copy_request_to_all_collections',
        agentFormPayload: payload,
        chatId: chatId ?? undefined,
      };
    case 'set_login_credentials':
      return {
        agentForm: 'set_login_credentials',
        agentFormPayload: payload,
        chatId: chatId ?? undefined,
      };
    case 'create_custom_function':
      return { agentForm: 'create_function', agentFormPayload: payload, chatId: chatId ?? undefined };
    default:
      return null;
  }
}
